import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, unref as _unref } from "vue"

const _hoisted_1 = { class: "auto-job-card" }
const _hoisted_2 = { class: "auto-job-card__header" }
const _hoisted_3 = { class: "auto-job-card__header-label" }
const _hoisted_4 = { class: "auto-job-card__text" }
const _hoisted_5 = { class: "auto-job-card__field" }
const _hoisted_6 = { class: "auto-job-card__field-label" }
const _hoisted_7 = { class: "auto-job-card__field-title" }
const _hoisted_8 = { class: "auto-job-card__field" }
const _hoisted_9 = { class: "auto-job-card__field-label" }
const _hoisted_10 = { class: "auto-job-card__field-title" }
const _hoisted_11 = { class: "auto-job-card__footer" }
const _hoisted_12 = { class: "auto-job-card__footer-item" }
const _hoisted_13 = { class: "auto-job-card__footer-item" }
const _hoisted_14 = {
  key: 0,
  class: "auto-job-card__date auto-job-card__date--locked"
}
const _hoisted_15 = { class: "auto-job-card__tags" }

import {
	computed,
	ref
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue";
import AppDateString from "@/components/ui-kit/datepicker/AppDateString.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import type { SelectModelValue } from "@/components/ui-kit/select/AppSelect.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import type { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import {
	buildAuthorOptions,
	buildExecutorOptions,
	buildExecutorSummary,
	formatAutoJobDate
} from "@/utils/autoJobTemplates";
import type {
	AutoJobTemplateErrors,
	AutoJobTemplatePatch,
	AutoJobTemplateState
} from "@/utils/autoJobTemplates";

type AutoJobTemplateCardProps = {
	state: AutoJobTemplateState;
	errors: AutoJobTemplateErrors;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'AutoJobTemplateCard',
  props: {
    state: {},
    errors: {}
  },
  emits: ["update", "refresh-authors", "refresh-executors", "calendar-toggle"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null);

const authorOptions = computed(() =>
	buildAuthorOptions(props.state.source.AuthorHolders, props.state.authorKey));
const executorOptions = computed(() =>
	buildExecutorOptions(props.state.source.ExectutorHolders, props.state.executorKeys));
const executorSummary = computed(() =>
	buildExecutorSummary(props.state.source.ExectutorHolders, props.state.executorKeys));

const lockedTags = computed(() => props.state.source.LockedTags ?? []);
const lockedTagNames = computed(() => new Set(lockedTags.value.map(tag => tag.Name.trim().toLowerCase())));

const formattedDate = computed(() => formatAutoJobDate(props.state.date));

const onSelectedInput = (value: boolean) => {
	emit('update', { selected: value });
};

const onTextInput = (value: string) => {
	emit('update', { text: value });
};

const onAuthorChange = (value: SelectModelValue) => {
	if (value == null || Array.isArray(value)) {
		return;
	}
	emit('update', { authorKey: String(value) });
};

// Правило против дублей «роль + её участник» применяет composable
const onExecutorsChange = (value: SelectModelValue) => {
	const keys = Array.isArray(value) ? value.map(String) : [];
	emit('update', { executorKeys: keys });
};

const onNeedCheckingInput = (value: boolean) => {
	emit('update', { needChecking: value });
};

const onDateChange = (value: Date | null) => {
	emit('update', { date: value });
};

// Списки перечитываем, когда пользователь открывает невалидное поле: например, в соседней
// вкладке добавили человека в роль.
const onAuthorsOpen = () => {
	if (props.errors.author || props.state.needRefresh.authors) {
		emit('refresh-authors');
	}
};

const onExecutorsOpen = () => {
	if (props.errors.executors || props.state.needRefresh.executors) {
		emit('refresh-executors');
	}
};

const withoutLocked = (tags: TagWithFrequency[]): TagWithFrequency[] =>
	tags.filter(tag => !lockedTagNames.value.has(tag.Name.trim().toLowerCase()));

const onTagSelectorChange = (nextTags: TagWithFrequency[]) => {
	emit('update', { tags: withoutLocked(nextTags) });
};

const onCreateTag = (name: string) => {
	const normalized = name.trim().toLowerCase();
	if (!normalized) {
		return;
	}
	const current = props.state.tags;
	if (lockedTagNames.value.has(normalized) || current.some(tag => tag.Name.toLowerCase().trim() === normalized)) {
		return;
	}
	const tempId = Math.min(0, ...current.map(tag => tag.Id)) - 1;
	emit('update', {
		tags: [...current, {
			Id: tempId,
			Name: normalized,
			Type: TagBaseType.Common,
			Frequency: 0,
			FullName: normalized,
		}]
	});
};

const removeTag = (tag: TagWithFrequency) => {
	emit('update', {
		tags: props.state.tags.filter(t =>
			t.Id !== tag.Id || t.Name.toLowerCase() !== tag.Name.toLowerCase()
		)
	});
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(AppCheckbox, {
        value: _ctx.state.selected,
        disabled: _ctx.state.source.IsMandatory,
        onOnInput: onSelectedInput
      }, null, 8, ["value", "disabled"]),
      _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('autojob_create_job')), 1),
      (_ctx.errors.template)
        ? (_openBlock(), _createBlock(AppTooltip, {
            key: 0,
            class: "auto-job-card__icon",
            text: _ctx.$t(_ctx.errors.template)
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                name: "invalid",
                width: "16px",
                color: "#D05A4F"
              })
            ]),
            _: 1
          }, 8, ["text"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(AppCustomTextarea, {
        "model-value": _ctx.state.text,
        disabled: _ctx.state.source.TextLocked,
        placeholder: _ctx.$t('job_message_placeholder'),
        "min-rows": 1,
        "max-rows": 6,
        "onUpdate:modelValue": onTextInput
      }, null, 8, ["model-value", "disabled", "placeholder"]),
      (_ctx.errors.text)
        ? (_openBlock(), _createBlock(AppTooltip, {
            key: 0,
            class: _normalizeClass({
					'auto-job-card__text-error': true,
					'auto-job-card__text-error--shifted': !!_ctx.state.text && !_ctx.state.source.TextLocked
				}),
            text: _ctx.$t(_ctx.errors.text)
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                name: "invalid",
                width: "16px",
                color: "#D05A4F"
              })
            ]),
            _: 1
          }, 8, ["class", "text"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t('autojob_author')), 1),
        (_ctx.state.source.AuthorLocked)
          ? (_openBlock(), _createBlock(AppTooltip, {
              key: 0,
              class: "auto-job-card__icon",
              text: _ctx.$t('autojob_field_locked')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "lock",
                  width: "16px",
                  color: "#979797"
                })
              ]),
              _: 1
            }, 8, ["text"]))
          : _createCommentVNode("", true),
        (_ctx.errors.author)
          ? (_openBlock(), _createBlock(AppTooltip, {
              key: 1,
              class: "auto-job-card__icon",
              text: _ctx.$t(_ctx.errors.author)
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "invalid",
                  width: "16px",
                  color: "#D05A4F"
                })
              ]),
              _: 1
            }, 8, ["text"]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(AppSelect, {
        "model-value": _ctx.state.authorKey,
        items: authorOptions.value,
        placeholder: _ctx.$t('not_selected_label'),
        disabled: _ctx.state.source.AuthorLocked,
        "description-key": "description",
        "description-tooltip-max-width": "320px",
        searchable: "",
        "onUpdate:modelValue": onAuthorChange,
        onOpen: onAuthorsOpen
      }, null, 8, ["model-value", "items", "placeholder", "disabled"])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t('autojob_executor')), 1),
        (_ctx.state.source.ExecutorLocked)
          ? (_openBlock(), _createBlock(AppTooltip, {
              key: 0,
              class: "auto-job-card__icon",
              text: _ctx.$t('autojob_field_locked')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "lock",
                  width: "16px",
                  color: "#979797"
                })
              ]),
              _: 1
            }, 8, ["text"]))
          : _createCommentVNode("", true),
        (_ctx.errors.executors)
          ? (_openBlock(), _createBlock(AppTooltip, {
              key: 1,
              class: "auto-job-card__icon",
              text: _ctx.$t(_ctx.errors.executors)
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "invalid",
                  width: "16px",
                  color: "#D05A4F"
                })
              ]),
              _: 1
            }, 8, ["text"]))
          : _createCommentVNode("", true)
      ]),
      _createVNode(AppSelect, {
        "model-value": _ctx.state.executorKeys,
        items: executorOptions.value,
        placeholder: _ctx.$t('not_selected_label'),
        "dropdown-label": executorSummary.value || _ctx.$t('not_selected_label'),
        disabled: _ctx.state.source.ExecutorLocked,
        "description-key": "description",
        "description-tooltip-max-width": "320px",
        multiple: "",
        searchable: "",
        "onUpdate:modelValue": onExecutorsChange,
        onOpen: onExecutorsOpen
      }, null, 8, ["model-value", "items", "placeholder", "dropdown-label", "disabled"])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, [
        _createVNode(AppCheckbox, {
          value: _ctx.state.needChecking,
          disabled: _ctx.state.source.NeedCheckingLocked,
          onOnInput: onNeedCheckingInput
        }, null, 8, ["value", "disabled"]),
        _createElementVNode("span", {
          class: _normalizeClass({
						'auto-job-card__footer-label': true,
						'auto-job-card__footer-label--disabled': _ctx.state.source.NeedCheckingLocked
					})
        }, _toDisplayString(_ctx.$t('job_with_report')), 3)
      ]),
      _createElementVNode("div", _hoisted_13, [
        (_ctx.state.source.DateLocked)
          ? (_openBlock(), _createElementBlock("span", _hoisted_14, [
              _createVNode(AppIcon, {
                name: "clock",
                width: "18px",
                color: "#3F884F"
              }),
              _createElementVNode("span", null, _toDisplayString(formattedDate.value), 1)
            ]))
          : (_openBlock(), _createBlock(AppDateString, {
              key: 1,
              "model-value": _ctx.state.date,
              "onUpdate:modelValue": onDateChange,
              onOpen: _cache[0] || (_cache[0] = ($event: any) => (emit('calendar-toggle', true))),
              onClose: _cache[1] || (_cache[1] = ($event: any) => (emit('calendar-toggle', false)))
            }, {
              trigger: _withCtx(() => [
                _createElementVNode("span", {
                  class: _normalizeClass({
								'auto-job-card__date': true,
								'auto-job-card__date--empty': !_ctx.state.date
							})
                }, [
                  _createVNode(AppIcon, {
                    name: "clock",
                    width: "18px",
                    color: _ctx.state.date ? '#3F884F' : '#979797'
                  }, null, 8, ["color"]),
                  _createElementVNode("span", null, _toDisplayString(formattedDate.value || _ctx.$t('ui_date_placeholder')), 1)
                ], 2)
              ]),
              _: 1
            }, 8, ["model-value"])),
        (_ctx.errors.date)
          ? (_openBlock(), _createBlock(AppTooltip, {
              key: 2,
              class: "auto-job-card__icon",
              text: _ctx.$t(_ctx.errors.date)
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "invalid",
                  width: "16px",
                  color: "#D05A4F"
                })
              ]),
              _: 1
            }, 8, ["text"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_15, [
        (lockedTags.value.length)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lockedTags.value, (tag) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: `locked-${tag.Id}-${tag.Name}`,
                  class: "auto-job-card__tag auto-job-card__tag--locked"
                }, "#" + _toDisplayString(tag.Name.toLowerCase().trim()), 1))
              }), 128)),
              _createVNode(AppTooltip, {
                class: "auto-job-card__icon",
                text: _ctx.$t('autojob_field_locked')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "lock",
                    width: "16px",
                    color: "#D2D2D2"
                  })
                ]),
                _: 1
              }, 8, ["text"])
            ], 64))
          : _createCommentVNode("", true),
        _createVNode(AppResizableDropdown, {
          ref_key: "$tagsDropdown",
          ref: $tagsDropdown,
          position: "right",
          "close-by-click-outside": true,
          "max-width": 640,
          "max-height": 460,
          "content-z-index": 10003,
          "mobile-title": _ctx.$t('choose_tags')
        }, {
          activator: _withCtx(() => [
            _createVNode(AppTooltip, {
              text: _ctx.$t('file_add_tag')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  class: "auto-job-card__tags-add",
                  name: "hash",
                  width: "18px",
                  color: "#979797",
                  "hover-color": "#499259",
                  cursor: "pointer"
                })
              ]),
              _: 1
            }, 8, ["text"])
          ]),
          default: _withCtx(() => [
            _createVNode(AppTagSelector, {
              "model-value": _ctx.state.tags,
              "entity-type": _unref(TagEntityType).Job,
              mode: "select",
              "allow-create": "",
              "onUpdate:modelValue": onTagSelectorChange,
              onCreateTag: onCreateTag,
              onClose: _cache[2] || (_cache[2] = ($event: any) => ($tagsDropdown.value?.close()))
            }, null, 8, ["model-value", "entity-type"])
          ]),
          _: 1
        }, 8, ["mobile-title"]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.state.tags, (tag) => {
          return (_openBlock(), _createElementBlock("span", {
            key: tag.Id || tag.Name,
            class: "auto-job-card__tag"
          }, [
            _createElementVNode("span", null, _toDisplayString(tag.Name.toLowerCase().trim()), 1),
            _createVNode(AppTooltip, {
              text: _ctx.$t('ui_tag_detach')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  class: "auto-job-card__tag-delete",
                  name: "close-bold",
                  width: "12px",
                  color: "#D2D2D2",
                  "hover-color": "#BA4C4C",
                  cursor: "pointer",
                  onClick: ($event: any) => (removeTag(tag))
                }, null, 8, ["onClick"])
              ]),
              _: 2
            }, 1032, ["text"])
          ]))
        }), 128)),
        (_ctx.errors.tags)
          ? (_openBlock(), _createBlock(AppTooltip, {
              key: 1,
              class: "auto-job-card__icon",
              text: _ctx.$t(_ctx.errors.tags)
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  name: "invalid",
                  width: "16px",
                  color: "#D05A4F"
                })
              ]),
              _: 1
            }, 8, ["text"]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})